import React, { ChangeEvent, FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Colors } from '../../../styles/colors';
import { Label } from './Label';
import { isNotEmpty } from '../../../utils/Validators';

export interface InputProps {
  id: string;
  label?: string;
  hint?: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  type?: string;
  value?: string;
  required?: boolean;
  disabled?: boolean;
  errorMessage?: string;
  hasError?: boolean;
  validate?: (value: string) => boolean;
  whiteBackground?: boolean;
}

export const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-top: 1rem;
`;

export const InputInnerWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: stretch;
`;

export const StyledInput = styled.input<{ whiteBackground: boolean }>`
  border: none;
  min-height: 3rem;
  color: ${Colors.grayDark};
  background-color: ${(props) => props.whiteBackground ? Colors.white : Colors.background};
  font-size: 1rem;
  padding: 0 1rem;
  z-index: 2;
  margin: 0;
  flex: 1;

  ::placeholder,
  ::-webkit-input-placeholder {
    color: ${Colors.gray};
    font-size: 1rem;
  }

  &:focus {
    outline: none;
  }

  &.local-error {
    box-shadow: inset 0 0 0 1px ${Colors.error};
    padding-left: 1rem;
    color: ${Colors.error};
  }

  &:disabled {
    pointer-events: none;
    cursor: default;
  }
`;

export const ErrorText = styled.div`
  font-size: 0.75rem;
  margin-top: 0.5rem;
  align-self: flex-start;
  color: ${Colors.error};
`;

export const DefaultInput: FC<InputProps> = (props) => {
  const {
    id,
    label = '',
    hint,
    onChange,
    placeholder = 'Hier eingeben...',
    type = 'text',
    value,
    required = false,
    disabled = false,
    errorMessage = 'Ungültiger Wert',
    hasError = false,
    validate = isNotEmpty,
    whiteBackground = false,
  } = props;
  const [localError, setLocalError] = useState<boolean>(hasError);

  useEffect(() => {
    setLocalError(hasError);
  }, [hasError]);

  useEffect(() => {
    if (value && value.length > 0) {
      setLocalError(false);
    }
  }, [value]);

  const handleLocalError = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (required && validate(event.target.value) == false) {
      setLocalError(true);
    } else {
      setLocalError(false);
    }
  };

  return (
    <InputWrapper>
      {
        props.label &&
        <Label
          hint={hint}
          for={props.id}
        >{props.label}</Label>
      }
      <InputInnerWrapper>
        <StyledInput
          whiteBackground={whiteBackground}
          className={`${localError ? 'local-error' : ''}`}
          type={`${props.type ? props.type : 'text'}`}
          id={props.id}
          value={value}
          disabled={disabled}
          placeholder={`${props.placeholder ? props.placeholder : 'Hier eingeben'}`}
          onBlur={(event: React.ChangeEvent<HTMLInputElement>) => {
            handleLocalError(event);
          }}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            handleLocalError(event);
            props.onChange(event);
          }}
        />
        {localError && <ErrorText>{errorMessage}</ErrorText>}
      </InputInnerWrapper>
    </InputWrapper>
  );
};
